/**
 * Protected Route Component
 *
 * Wrapper for routes that require authentication.
 * Redirects unauthenticated users to login page.
 * Optionally checks for specific permissions based on active role.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/ui/Spinner';
import { hasPermission } from '../lib/utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Permission required to access this route (e.g., "iam:user:read") */
  requiredPermission?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
}) => {
  const { isAuthenticated, isLoading, activeRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="xl" label="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ returnUrl: location.pathname + location.search }}
        replace
      />
    );
  }

  // Check permission requirement against active role
  if (requiredPermission && !hasPermission(activeRole?.permissions, requiredPermission)) {
    return <Navigate to="/app/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
